import React, { useMemo, useCallback } from 'react';
import { Box, Heading, Text, Loader, Divider } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useAtom, useAtomValue } from 'jotai';
import {
  SparklesSmall,
  CreditCardSmall,
  DesignSparklesSmall,
  MobileSmall,
  SiteContentSmall,
} from '@wix/wix-ui-icons-common';
import { isEmpty } from 'lodash';
import { Footer } from '../../../components/Footer';
import s from './MobileStoreName.scss';
import { AcceptTerms } from '../components/AcceptPayments/AcceptTerms';
import { selectedSuggestionAtom, suggestionsAtom, userRejectedPaymentsAtom } from '../atoms';
import { useSuggestStoreName } from '../api/mutations';
import { usePaymentAcceptance } from '../hooks/usePaymentAcceptance';
import { normalizeProducts } from '../utils';
import { useStoreNameInput } from '../hooks/useStoreNameInput';
import { useStoreLaunch } from '../hooks/useStoreLaunch';
import { useStoreName, useSelectedCategory } from '../../../store/appStore';
import { StoreNameInput } from '../components/StoreNameInput';
import { dataHooks } from '../datahooks';
import { ProgressBar } from '../../../components/ProgressBar/ProgressBar';
import { DynamicSelector } from '../../../components/DynamicSelector/DynamicSelector';
import { isThemeLoadingAtom } from '../../templateSelection/atoms';
import { useAppNavigation } from '../../../hooks/useAppNavigation';
import { useSpec } from '../../../hooks/useSpec';
import { DomainFlow } from '../../../experiments';
import { DOMAIN_URL, SITE_PREVIEW_URL } from '../../../consts';

const getLoaderSteps = (isShowPayments: boolean) => {
  const steps = [
    {
      label: 'CREATING_YOUR_SITE',
      icon: <SiteContentSmall />,
    },
    {
      label: 'OPTIMIZE_TO_MOBILE',
      icon: <MobileSmall />,
    },
    {
      label: 'APPLYING_THEME',
      icon: <DesignSparklesSmall />,
    },
  ];

  if (isShowPayments) {
    steps.push({
      label: 'CONNECTING_PAYMENTS',
      icon: <CreditCardSmall />,
    });
  }
  return steps;
};

export function MobileStoreName() {
  const { t } = useTranslation();
  const { navigate } = useAppNavigation();
  const isDomainFlowEnabled = useSpec(DomainFlow);

  const storeName = useStoreName();
  const selectedCategory = useSelectedCategory();
  const isThemeLoading = useAtomValue(isThemeLoadingAtom);
  const [selectedSuggestion] = useAtom(selectedSuggestionAtom);
  const [suggestions, setSuggestions] = useAtom(suggestionsAtom);
  const [userRejectedPayments, setUserRejectedPayments] = useAtom(userRejectedPaymentsAtom);

  const { handleStoreNameChange, handleSuggestionSelect } = useStoreNameInput();
  const { launchStore, isPending, isSuccess } = useStoreLaunch();
  const { mutate: fetchSuggestions, isPending: isLoadingSuggestions } = useSuggestStoreName();
  const { isPaymentsAccepted, isShowAcceptPayments: isShowPayments } = usePaymentAcceptance();

  function handleGetSuggestions() {
    if (!selectedCategory) {
      return;
    }

    fetchSuggestions(
      {
        category: selectedCategory,
        products: normalizeProducts(selectedCategory),
        previousResults: suggestions,
      },
      {
        onSuccess: (data) => {
          setSuggestions(data);
        },
      },
    );
  }

  function handleAcceptPaymentsChange() {
    setUserRejectedPayments(!userRejectedPayments);
  }

  const steps = useMemo(() => getLoaderSteps(isPaymentsAccepted), [isPaymentsAccepted]);

  const handleProgressComplete = useCallback(() => {
    const nextUrl = isDomainFlowEnabled ? DOMAIN_URL : SITE_PREVIEW_URL;
    navigate(nextUrl);
  }, [isDomainFlowEnabled, navigate]);

  const isLoading = isLoadingSuggestions && isEmpty(suggestions);

  if (isPending || isSuccess) {
    return (
      <Box align="center" height="100%" width="100%">
        <ProgressBar steps={steps} onComplete={handleProgressComplete} isLoading={isPending} />
      </Box>
    );
  }

  const suggestionOptions = suggestions.map((suggestion) => ({
    value: suggestion,
    label: suggestion,
  }));

  return (
    <Box direction="vertical" height="100%" width="100%" gap={2} className={s.container}>
      <Box direction="vertical" className={s.contentWrapper} gap={4}>
        <Box direction="vertical" gap={1}>
          <Heading className={s.title} size="medium">
            {t('WHAT_YOUR_STORE_NAME')}
          </Heading>

          <Text>{t('PICK_MEMORABLE_NAME_TO_REPRESENT_YOUR_BRAND')}</Text>

          <Text className={s.textDisclaimer}>
            {t('WELL_CREATE_A_CONSISTENT_BRAND_BY_USING_YOUR_STORE_NAME_ACROSS_YOUR_SITE')}
          </Text>
        </Box>

        <StoreNameInput value={storeName} onChange={handleStoreNameChange} />

        {isLoading ? (
          <Loader size="large" />
        ) : (
          <Box direction="vertical" gap={2}>
            <DynamicSelector
              options={suggestionOptions}
              onSelect={handleSuggestionSelect}
              selectedValue={selectedSuggestion}
              dataHook={dataHooks.StoreNameSuggestion}
              label={`${t('SUGGESTIONS')}:`}
              actionText={t('GENERATE_NEW_SUGGESTIONS')}
              actionIcon={<SparklesSmall />}
              onAction={handleGetSuggestions}
              isLoading={isLoadingSuggestions}
            />

            <Text size="tiny" secondary>
              {t('ai_disclaimer')}
            </Text>
          </Box>
        )}

        <Divider />

        <AcceptTerms
          onCheckboxChange={handleAcceptPaymentsChange}
          isAccepted={isPaymentsAccepted}
          isShowPayments={isShowPayments}
        />
      </Box>

      <Footer
        isDisabled={!storeName}
        isLoading={isThemeLoading}
        onClick={launchStore}
        nextButtonText={t('store_name.cta.button')}
        showSuffixIcon={false}
        className={s.templateFooter}
      />
    </Box>
  );
}
